import React, { memo, useState, useEffect, Suspense } from 'react';
import { motion } from 'framer-motion';
import HeroParticles from './HeroParticles';
import '../styles/hero-buttons.css';

const HeroSection: React.FC = memo(() => {
  const [show3D, setShow3D] = useState(false);

  useEffect(() => {
    // Delay 3D rendering to allow page transition to finish smoothly
    const timer = setTimeout(() => {
      setShow3D(true);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const heroStyles = {
    section: {
      position: 'relative' as const,
      top: 0,
      zIndex: 1,
      width: '100%',
      minHeight: '100vh',
      margin: 0,
      backgroundColor: '#000000',
      overflow: 'hidden' as const,
      willChange: 'transform'
    },
    backgroundPattern: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      // backgroundImage: `url(${ASSETS.pattern})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center top',
      backgroundSize: '120% auto',
      opacity: 0.2,
      zIndex: 1,
      pointerEvents: 'none' as const
    },
    noiseOverlay: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
      opacity: 0.12,
      mixBlendMode: 'overlay' as const,
      zIndex: 2,
      pointerEvents: 'none' as const
    }
  };

  return (
    <>
      <style>{`
        .hero-content-wrapper {
          display: grid;
          grid-template-columns: 1fr 500px;
          gap: 2rem;
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          position: relative;
          align-items: center;
          padding: 0 2rem;
          padding-top: 0;
        }
        
        .hero-text-content {
          position: relative;
          z-index: 20;
          width: 100%;
          max-width: 100%;
        }
        
        .hero-text-content h1,
        .hero-text-content p {
          text-align: left;
        }
        
        .hero-sphere-content {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
        }
        
        /* Tablet und kritischer Übergangsbereich */
        @media (min-width: 769px) and (max-width: 1200px) {
          .hero-content-wrapper {
            padding-top: 0;
          }
        }
        
        @media (max-width: 1024px) {
          .hero-content-wrapper {
            grid-template-columns: 1fr;
            gap: 2rem;
            place-items: center;
            padding: 0 1rem;
            padding-top: 0;
          }
          
          .hero-text-content {
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          
          .hero-text-content h1,
          .hero-text-content p {
            text-align: center;
          }
          
          .hero-buttons {
            justify-content: center !important;
          }
          
          .hero-sphere-content {
            max-width: 90vw;
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
          }
        }
        
        @media (max-width: 640px) {
          .hero-content-wrapper {
            padding: 0 1rem;
          }
          
          .hero-text-content {
            text-align: center;
          }
          
          .hero-text-content h1 {
            font-size: clamp(1.25rem, 6vw, 2rem) !important;
            text-align: center !important;
          }
          
          .hero-text-content p {
            font-size: clamp(0.875rem, 3vw, 1rem) !important;
            text-align: center !important;
          }
          
          .hero-sphere-content {
            max-width: 50vw !important;
            width: 50vw !important;
            margin: 0 auto;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          
          .hero-buttons {
            flex-direction: column !important;
            width: 100% !important;
            align-items: center !important;
            justify-content: center !important;
          }
        }
      `}</style>
      <section id="hero" style={heroStyles.section}>
        <div style={heroStyles.backgroundPattern} />
        <div style={heroStyles.noiseOverlay} />

        <div style={{ 
          width: '100%',
          maxWidth: 'none',
          margin: '0',
          padding: '0 0 0 0',
          position: 'relative',
          zIndex: 10,
          pointerEvents: 'none'
        }}>
          <div className="hero-content-wrapper">
            
            {/* Text Content */}
            <div className="hero-text-content" style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              zIndex: 20,
              pointerEvents: 'auto',
              width: '100%'
            }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                style={{ marginBottom: '1rem', position: 'relative', width: '100%' }}
              >
                <h1 style={{ 
                  fontSize: 'clamp(1.5rem, 5vw, 3.5rem)',
                  fontWeight: 'bold',
                  color: 'white',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                  margin: 0
                }}>
                  Wir bauen digitale<br />
                  Produkte, die funktionieren.
                </h1>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                style={{ 
                  color: '#9ca3af',
                  fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                  lineHeight: 1.6,
                  marginBottom: '1.5rem',
                  maxWidth: '80rem'
                }}
              >
                Wir sind StimmDigital.<br/>Dein Tech-Partner aus Berlin. Wir entwickeln{' '}
                <span style={{ color: '#77F76C', fontSize: '100%' }}>maßgeschneiderte Software</span>, Apps und Websites für Startups und Unternehmen. Direkt, persönlich und ohne Schnickschnack.
              </motion.p>

              <motion.div
                className="hero-buttons"
                style={{ 
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '1rem',
                  width: '100%',
                  justifyContent: 'flex-start',
                  flexWrap: 'wrap',
                  maxWidth: '600px'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <button 
                  className="hero-btn-primary pulse"
                  data-action="contact"
                  onClick={() => {
                    const event = new CustomEvent('contactButtonClick');
                    document.dispatchEvent(event);
                  }}
                >
                  <span className="btn-content">
                    <i className="fas fa-calendar btn-icon"></i>
                    Kostenloses Beratungsgespräch
                  </span>
                </button>
                <button 
                  className="hero-btn-secondary"
                  onClick={() => {
                    window.scrollBy({
                      top: window.innerHeight,
                      behavior: 'smooth'
                    });
                  }}
                >
                  <span className="btn-content">
                    Mehr erfahren
                  </span>
                </button>
              </motion.div>
              {/*}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                style={{ marginTop: '1.25rem' }}
              >
                <img
                  src={dsgvoBadge}
                  alt="DSGVO konform"
                  style={{ height: '48px', width: 'auto', opacity: 0.45 }}
                />
              </motion.div>
              */}
            </div>

            {/* 3D Sphere */}
            <div className="hero-sphere-content">
              <motion.div
                initial={{ opacity: 0, scale: 1 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2, type: "spring" }}
                style={{ 
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'auto'
                }}
              >
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  {show3D && (
                    <Suspense fallback={null}>
                      <HeroParticles />
                    </Suspense>
                  )}
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
});

HeroSection.displayName = 'HeroSection';

export default HeroSection;

